import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowLeft, Tag, Info, ShieldCheck, ChevronRight, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button'
import { useToast } from '../components/ui/use-toast';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

import { SITE_BASE_URL } from '../data/productCategories';

// Breadcrumb Component
const Breadcrumb = () => (
  <nav aria-label="Breadcrumb" className="mb-6">
    <ol className="flex items-center space-x-2 text-sm text-gray-400">
      <li>
        <Link to="/" className="hover:text-[#ff6b35] transition-colors flex items-center">
          <Home size={14} className="mr-1" />
          Home
        </Link>
      </li>
      <li><ChevronRight size={14} /></li>
      <li>
        <Link to="/#products" className="hover:text-[#ff6b35] transition-colors">
          Product categories
        </Link>
      </li>
      <li><ChevronRight size={14} /></li>
      <li className="text-[#ff6b35] font-medium" aria-current="page">
        Planetary Gear (41A0002)
      </li>
    </ol>
  </nav>
);

const CategoryDetail = () => {
  const { toast } = useToast();

  const handleRequestQuote = (productName, productCode) => {
    toast({
      title: "Quote Request Initiated",
      description: `Inquiry for "${productName}" (Code: ${productCode}). Our team will contact you shortly.`,
    });
  };

  const product = {
    name: "Planetary Gear",
    code: "41A0002",
    category: "Engine Parts",
    image: "https://horizons-cdn.hostinger.com/44224aab-49f8-4832-a1a7-2f385d4dc6c7/136457bbbd24c1e79df043635e261c4d.jpg",
    description: "High-precision planetary gear designed for optimal torque transmission and durability in heavy-duty engine applications. Engineered to meet strict OEM specifications for reliability and performance in industrial environments.",
    features: [
      "Precision engineered for smooth operation",
      "High-grade steel construction",
      "Heat-treated for maximum durability",
      "Direct OEM replacement fit"
    ]
  };

  const baseUrl = SITE_BASE_URL;
  const currentUrl = `${baseUrl}/category/planetary-gear-41a0002`;

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": `${product.name} - ${product.code}`,
    "image": product.image,
    "description": product.description,
    "sku": product.code,
    "mpn": product.code,
    "brand": {
      "@type": "Brand",
      "name": "Trade Zone Equipments"
    },
    "category": product.category,
    "offers": {
      "@type": "Offer",
      "url": currentUrl,
      "priceCurrency": "USD",
      "price": "0.00",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": baseUrl
    },{
      "@type": "ListItem",
      "position": 2,
      "name": "Product categories",
      "item": `${baseUrl}#products`
    },{
      "@type": "ListItem",
      "position": 3,
      "name": `${product.name} (${product.code})`,
      "item": currentUrl
    }]
  };

  return (
    <>
      <Helmet>
        <title>{`${product.name} ${product.code} - Heavy Duty Engine Parts | Trade Zone Equipments`}</title>
        <meta name="description" content={`Buy ${product.name} (Code: ${product.code}) for heavy machinery. ${product.description.substring(0, 150)}...`} />
        <meta name="keywords" content={`Planetary Gear, ${product.code}, Engine Parts, Heavy Machinery Parts, OEM Replacement, Transmission Gear`} />
        <link rel="canonical" href={currentUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={`${product.name} ${product.code} - Premium Engine Parts`} />
        <meta property="og:description" content={`High-performance ${product.name} designed for durability. In stock now.`} />
        <meta property="og:image" content={product.image} />
        <meta property="product:brand" content="Trade Zone Equipments" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#1a1a1a] flex flex-col font-sans">
        <Navigation />
        
        <main className="flex-grow pt-24 pb-16 px-4 sm:px-6 lg:px-8">
          <article className="max-w-7xl mx-auto" itemScope itemType="https://schema.org/Product">
            
            <Breadcrumb />

            {/* Header Section */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-12 border-b border-[#2d2d2d] pb-8"
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                   <span className="text-[#ff6b35] font-semibold text-sm tracking-wider uppercase mb-2 block">
                     {product.category}
                   </span>
                   <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2" itemProp="name">
                     {product.name} - <span className="text-[#ff6b35]">{product.code}</span>
                   </h1>
                </div>
                <div className="flex items-center gap-2">
                   <div className="bg-[#2d2d2d] px-4 py-2 rounded-lg border border-[#3d3d3d]">
                     <span className="text-gray-400 text-xs uppercase block">Product Code</span>
                     <span className="text-white font-mono font-bold" itemProp="sku">{product.code}</span>
                   </div>
                </div>
              </div>
            </motion.header>

            {/* Product Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              
              {/* Product Image Section */}
              <section aria-label="Product Images">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="relative rounded-2xl overflow-hidden bg-[#2d2d2d] shadow-2xl border border-[#3d3d3d] group"
                >
                  <div className="aspect-w-4 aspect-h-3 bg-white/5">
                    <img
                      src={product.image}
                      alt={`Planetary Gear - Code ${product.code} - Front View`}
                      title={`${product.name} ${product.code}`}
                      loading="lazy"
                      width="800"
                      height="600"
                      itemProp="image"
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-[#ff6b35] text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-1">
                      <Tag size={14} aria-hidden="true" />
                      In Stock
                    </span>
                  </div>
                </motion.div>
              </section>

              {/* Product Details Section */}
              <section className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-4">Product Overview</h2>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6" itemProp="description">
                    {product.description}
                  </p>
                </div>

                <div className="bg-[#2d2d2d]/50 rounded-xl p-6 border border-[#3d3d3d]">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Info size={18} className="text-[#ff6b35]" aria-hidden="true" />
                    Technical Specifications
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-400 text-sm">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#ff6b35] shrink-0" aria-hidden="true" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-[#2d2d2d]">
                   <div className="flex flex-col sm:flex-row gap-4 items-center mb-4">
                     <div className="flex-1 w-full">
                       <Button
                        onClick={() => handleRequestQuote(product.name, product.code)}
                        className="w-full bg-[#ff6b35] hover:bg-[#ff8c42] text-white py-6 text-lg rounded-lg transition-all duration-300 shadow-lg shadow-[#ff6b35]/20 hover:shadow-[#ff6b35]/40"
                        aria-label={`Request quote for ${product.name}`}
                      >
                        Request Quote
                      </Button>
                     </div>
                   </div>
                   
                   <div className="flex items-center gap-2 text-gray-400 text-sm justify-center sm:justify-start">
                     <ShieldCheck size={18} className="text-[#ff6b35]" aria-hidden="true" />
                     <span>Original Equipment Manufacturer (OEM) Standards</span>
                   </div>
                </div>
              </section>
            </div>
          </article>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default CategoryDetail;